import React, { useEffect, useState } from 'react';
import { Button, Card, CardFooter, Col, Row } from 'reactstrap';
import * as Icon from 'react-feather';
import { useFormik } from 'formik';
import { useDispatch } from 'react-redux';
import { toZonedTime } from 'date-fns-tz';

import CallexLoader from '../../../components/CallexLoader';
import AdminHeader from '../../../components/header/AdminHeader';
import FormSelect from '../../../components/form/FormSelect';
import FormDateInput from '../../../components/form/FormDateInput';

import { useTimezone } from '../../../store/slices/commonSlice';
import { formatPayloadDate, formatTableDateTime } from '../../../utils/common';
import {
  vbBalanceGet,
  vbCallReportGet,
  vbCreditReportGet,
  useVbBalance
} from '../../../store/slices/voiceBroadcastBillingSlice';

const REPORT_TYPES = [
  { label: 'Credit Ledger', value: 'credit' },
  { label: 'Call Charges', value: 'call' }
];

const SLAB_LABEL = {
  '0-15': '1-15 sec',
  '16-30': '16-30 sec',
  '31-45': '31-45 sec',
  '46-60': '46+ sec'
};

const STATUS_LABEL = {
  charged: 'Charged',
  not_answered: 'Not answered (free)',
  insufficient_credit: 'Not charged - low credit',
  no_account: 'No credit account',
  error: 'Error'
};

const fmt = (v) =>
  Number(v || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

// Tenant view of VoiceBroadcast prepaid credit.
//
// Cards show Total / Used / Pending. "Pending Credit" is the spendable balance
// (total - used) — what remains to dial with.
const VbCreditReport = () => {
  const dispatch = useDispatch();
  const timezone = useTimezone();
  const balance = useVbBalance();

  const today = toZonedTime(new Date(), timezone);
  const [isLoading, setIsLoading] = useState(false);
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState(null);
  const [slabSummary, setSlabSummary] = useState([]);
  const [reportType, setReportType] = useState('credit');

  // Default to TODAY only. The report auto-runs on open (see the mount effect
  // below), so the landing view answers "what happened today" immediately;
  // widening the range is a deliberate act via the filter.
  const initialValues = {
    report_type: 'credit',
    start_date: today,
    end_date: today
  };

  const fetchBalance = async () => {
    try {
      await dispatch(vbBalanceGet()).unwrap();
    } catch (error) {
      console.log('error', error);
    }
  };

  const onSubmit = async (valuesToUse) => {
    const v = valuesToUse || values;
    setIsLoading(true);
    setIsFilterOpen(false);
    try {
      const params = {
        start_date: formatPayloadDate(v.start_date),
        end_date: formatPayloadDate(v.end_date),
        limit: 500
      };
      const isCall = v.report_type === 'call';
      const response = await dispatch(
        isCall ? vbCallReportGet(params) : vbCreditReportGet(params)
      ).unwrap();

      setReportType(v.report_type);
      setRows(response?.data || []);
      setSummary(response?.summary || null);
      setSlabSummary(isCall ? response?.slab_summary || [] : []);
    } catch (error) {
      console.log('error', error);
      setRows([]);
    } finally {
      setIsLoading(false);
    }
  };

  const { values, handleBlur, handleSubmit, touched, errors, setFieldValue } = useFormik({
    initialValues,
    onSubmit: () => onSubmit(values)
  });

  useEffect(() => {
    fetchBalance();
    onSubmit(initialValues);
  }, []);

  const pending = Number(balance?.pending_credit || 0);

  return (
    <>
      <AdminHeader
        title="VoiceBroadcast Credit"
        isSearch
        onSearchClick={() => setIsFilterOpen(!isFilterOpen)}
        isRefresh
        onRefreshClick={() => {
          fetchBalance();
          onSubmit(values);
        }}
      />

      {/* ---- Total / Used / Pending ---- */}
      <Row className="dashboard-card-container">
        <Col lg="4">
          <Card
            className="report-card px-2"
            style={{ borderBottom: '2px solid #2cabe3', backgroundColor: '#2cabe333' }}>
            <div className="card-icon" style={{ backgroundColor: '#2cabe3' }}>
              <div className="icon mr-2 fs-5 text-white">
                <Icon.CreditCard size={22} />
              </div>
            </div>
            <div className="d-flex flex-column ps-3">
              <span className="fs-5">Total Credit</span>
              <span className="fs-3">{fmt(balance?.total_credit)}</span>
            </div>
          </Card>
        </Col>
        <Col lg="4" className="ps-0">
          <Card
            className="report-card px-2"
            style={{ borderBottom: '2px solid #fb9678', backgroundColor: '#fb967833' }}>
            <div className="card-icon" style={{ backgroundColor: '#fb9678' }}>
              <div className="icon mr-2 fs-5 text-white">
                <Icon.TrendingDown size={22} />
              </div>
            </div>
            <div className="d-flex flex-column ps-3">
              <span className="fs-5">Used Credit</span>
              <span className="fs-3">{fmt(balance?.used_credit)}</span>
            </div>
          </Card>
        </Col>
        <Col lg="4" className="ps-0">
          <Card
            className="report-card px-2"
            style={{
              borderBottom: `2px solid ${pending <= 0 ? '#f62d51' : '#2cd07e'}`,
              backgroundColor: pending <= 0 ? '#f62d5133' : '#2cd07e33'
            }}>
            <div
              className="card-icon"
              style={{ backgroundColor: pending <= 0 ? '#f62d51' : '#2cd07e' }}>
              <div className="icon mr-2 fs-5 text-white">
                <Icon.DollarSign size={22} />
              </div>
            </div>
            <div className="d-flex flex-column ps-3">
              <span className="fs-5">Pending Credit</span>
              <span className="fs-3">{fmt(balance?.pending_credit)}</span>
            </div>
          </Card>
        </Col>
      </Row>

      {balance?.billing_enabled === false && (
        <Card className="p-2 mb-3" style={{ backgroundColor: 'rgba(44,171,227,0.1)' }}>
          <div style={{ fontSize: '13px' }}>
            Prepaid billing is not enabled for your account yet, so VoiceBroadcast is not
            credit-limited. Contact your administrator to add credit.
          </div>
        </Card>
      )}
      {balance?.billing_enabled && pending <= 0 && (
        <Card className="p-2 mb-3" style={{ backgroundColor: 'rgba(246,45,81,0.1)' }}>
          <div style={{ fontSize: '13px' }} className="text-danger">
            Your credit is exhausted — VoiceBroadcast dialing is paused. Contact your administrator
            to add credit.
          </div>
        </Card>
      )}
      {balance?.billing_enabled && pending > 0 && balance?.low_balance && (
        <Card className="p-2 mb-3" style={{ backgroundColor: 'rgba(255,193,7,0.12)' }}>
          <div style={{ fontSize: '13px' }} className="text-warning">
            Low balance — {fmt(balance?.pending_credit)} credit remaining.
          </div>
        </Card>
      )}

      {/* ---- filter ---- */}
      {isFilterOpen && (
        <Card className="mb-3 p-3">
          <form onSubmit={handleSubmit}>
            <Row>
              <Col lg="3" md="4">
                <FormSelect
                  name="report_type"
                  label="Report"
                  placeholder="Select Report"
                  value={values.report_type}
                  options={REPORT_TYPES}
                  onChange={(e) => setFieldValue('report_type', e.target.value)}
                  onBlur={handleBlur}
                  touched={touched}
                  errors={errors}
                  menuPlacement="bottom"
                />
              </Col>
              <Col lg="3" md="4">
                <FormDateInput
                  name="start_date"
                  label="From Date"
                  dateFormat="yyyy-MM-dd"
                  maxDate={values.end_date}
                  value={values.start_date}
                  onChange={(e) => setFieldValue('start_date', e.target.value)}
                  onBlur={handleBlur}
                  touched={touched}
                  errors={errors}
                />
              </Col>
              <Col lg="3" md="4">
                <FormDateInput
                  name="end_date"
                  label="To Date"
                  dateFormat="yyyy-MM-dd"
                  minDate={values.start_date}
                  value={values.end_date}
                  onChange={(e) => setFieldValue('end_date', e.target.value)}
                  onBlur={handleBlur}
                  touched={touched}
                  errors={errors}
                />
              </Col>
            </Row>
            <CardFooter className="p-0 bg-white text-end">
              <Button color="primary" className="px-4" type="submit">
                Search
              </Button>
            </CardFooter>
          </form>
        </Card>
      )}

      {/* ---- call-charge summary ---- */}
      {reportType === 'call' && summary && !isLoading && (
        <Card className="p-3 mb-3">
          <Row style={{ fontSize: '14px' }}>
            <Col lg="3">
              Total charged: <strong>{fmt(summary.total_charged)}</strong>
            </Col>
            <Col lg="2">
              Charged calls: <strong>{summary.charged_calls}</strong>
            </Col>
            <Col lg="3">
              Unanswered (free): <strong>{summary.unanswered_calls}</strong>
            </Col>
            <Col lg="2">
              Billed secs: <strong>{summary.billed_seconds}</strong>
            </Col>
            <Col lg="2">
              Not charged:{' '}
              <strong className={summary.unpaid_calls > 0 ? 'text-danger' : ''}>
                {summary.unpaid_calls}
              </strong>
            </Col>
          </Row>
          {slabSummary.length > 0 && (
            <div className="mt-2 pt-2 border-top" style={{ fontSize: '13px' }}>
              {slabSummary.map((s, i) => (
                <span key={i} className="me-4">
                  {s.call_type === 'dynamic_ivr' ? 'Dynamic IVR' : 'Normal'}{' '}
                  {SLAB_LABEL[s.slab] || s.slab}: <strong>{s.calls}</strong> calls / {fmt(s.charged)}
                </span>
              ))}
            </div>
          )}
        </Card>
      )}

      {/* ---- table ---- */}
      {isLoading ? (
        <Card
          className="p-0 d-flex justify-content-center align-items-center m-0"
          style={{ height: 'calc(100vh - 420px)' }}>
          <CallexLoader />
        </Card>
      ) : rows.length > 0 ? (
        <Card className="p-0 position-relative m-0">
          <div className="daily-report-table" style={{ height: 'calc(100vh - 430px)' }}>
            <table className="report-list-table">
              {reportType === 'credit' ? (
                <>
                  <thead>
                    <tr>
                      <th>Date</th>
                      <th>Type</th>
                      <th className="text-end">Amount</th>
                      <th className="text-end">Balance Before</th>
                      <th className="text-end">Balance After</th>
                      <th>Destination / Reference</th>
                      <th>Description</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((r, i) => {
                      const isAdd = Number(r?.amount) > 0;
                      return (
                        <tr key={i}>
                          <td>{formatTableDateTime(r?.created_at)}</td>
                          <td>{isAdd ? 'Credit added' : 'Call charged'}</td>
                          <td
                            className={`text-end fw-bold ${isAdd ? 'text-success' : 'text-danger'}`}>
                            {isAdd ? '+' : ''}
                            {fmt(r?.amount)}
                          </td>
                          <td className="text-end">{fmt(r?.balance_before)}</td>
                          <td className="text-end">{fmt(r?.balance_after)}</td>
                          {/* For a charged call the reference is a call_uuid,
                              which tells an operator nothing. Show the number
                              that was dialled and keep the uuid on hover for
                              anyone tracing it in call_history. */}
                          <td title={r?.reference_id || ''}>
                            {r?.destination_number ? (
                              <>
                                <div>{r.destination_number}</div>
                                {r?.crm_id && (
                                  <div className="text-muted" style={{ fontSize: '11px' }}>
                                    CRM {r.crm_id}
                                  </div>
                                )}
                              </>
                            ) : (
                              r?.reference_id || '-'
                            )}
                          </td>
                          <td>{r?.description || '-'}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </>
              ) : (
                <>
                  <thead>
                    <tr>
                      <th>Date</th>
                      <th>Phone</th>
                      <th>Type</th>
                      <th className="text-center">Answered Secs</th>
                      <th className="text-center">Slab</th>
                      <th className="text-end">Charge</th>
                      <th>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((r, i) => (
                      <tr key={i}>
                        <td>{formatTableDateTime(r?.created_at)}</td>
                        <td>{r?.phone_number || '-'}</td>
                        <td>{r?.call_type === 'dynamic_ivr' ? 'Dynamic IVR' : 'Normal'}</td>
                        <td className="text-center">{r?.billsec ?? 0}</td>
                        <td className="text-center">
                          {r?.applicable_slab ? SLAB_LABEL[r.applicable_slab] || r.applicable_slab : '-'}
                        </td>
                        <td className="text-end fw-bold">{fmt(r?.charge)}</td>
                        <td
                          className={
                            r?.status === 'insufficient_credit'
                              ? 'text-danger'
                              : r?.status === 'not_answered'
                                ? 'text-muted'
                                : ''
                          }>
                          {STATUS_LABEL[r?.status] || r?.status}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </>
              )}
            </table>
          </div>
        </Card>
      ) : (
        <Card
          className="p-0 d-flex justify-content-center align-items-center no-data m-0"
          style={{ height: 'calc(100vh - 420px)' }}>
          No data available
        </Card>
      )}
    </>
  );
};

export default VbCreditReport;
